import { useSelector, useDispatch } from 'react-redux';
import { toggleTheme, toggleMobileMenu, setActiveModule } from '@store/slices/uiSlice';
import { setSelectedConstruction, selectAllConstructions } from '@store/slices/constructionSlice';
import { logout } from '@store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import {
    Menu,
    Moon,
    Sun,
    LogOut,
    User,
    Settings,
    Building2,
    ChevronDown,
    Check,
    Wallet,
    HardHat,
} from 'lucide-react';
import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';
import { NotificationBadge, NotificationDropdown } from '@components/notifications';

export default function Header() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { sidebarCollapsed, theme, activeModule } = useSelector((state) => state.ui);
    const { user, role } = useSelector((state) => state.auth);
    const { constructions, selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [constructionMenuOpen, setConstructionMenuOpen] = useState(false);
    const [notificationOpen, setNotificationOpen] = useState(false);

    const userMenuRef = useRef(null);
    const constructionMenuRef = useRef(null);
    const notificationRef = useRef(null);

    // Close menus on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setUserMenuOpen(false);
            }
            if (constructionMenuRef.current && !constructionMenuRef.current.contains(event.target)) {
                setConstructionMenuOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setNotificationOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        dispatch(logout());
        navigate('/giris');
    };

    const openMobileMenu = () => {
        dispatch(toggleMobileMenu());
    };

    const handleConstructionSelect = (construction) => {
        dispatch(setSelectedConstruction(construction));
        setConstructionMenuOpen(false);
    };

    const handleSelectAll = () => {
        dispatch(selectAllConstructions());
        setConstructionMenuOpen(false);
    };

    const handleModuleChange = (module) => {
        dispatch(setActiveModule(module));
        navigate(module === 'finans' ? '/finans/ozet' : '/insaat/ozet');
    };

    const handleMarkAllRead = () => {
        console.log('Tümü okundu işaretlendi');
    };

    const currentConstructionName = showAllConstructions
        ? 'Tüm İnşaatlar'
        : selectedConstruction?.name || 'İnşaat Seç';

    // Admin hem finans hem inşaat görebilir, şef sadece inşaat
    const canSwitchModules = role === 'admin';

    return (
        <header
            className={clsx(
                'fixed top-0 right-0 h-[var(--header-height)] bg-bg-secondary border-b border-border-light',
                'flex items-center justify-between px-4 md:px-6 z-30 transition-all duration-300',
                'left-0 md:left-[var(--sidebar-collapsed-width)]',
                !sidebarCollapsed && 'md:left-[var(--sidebar-width)]'
            )}
        >
            {/* Left Side */}
            <div className="flex items-center gap-2 md:gap-4">
                {/* Mobile Menu Button */}
                <button
                    onClick={openMobileMenu}
                    className="p-2 rounded-lg hover:bg-bg-hover transition-colors md:hidden"
                >
                    <Menu size={24} className="text-text-secondary" />
                </button>

                {/* Module Selector (Admin only) */}
                {canSwitchModules ? (
                    <div className="flex items-center bg-bg-tertiary rounded-lg p-1 border border-border-light">
                        <button
                            onClick={() => handleModuleChange('finans')}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all duration-200',
                                activeModule === 'finans'
                                    ? 'bg-gradient-to-r from-primary to-primary-dark text-white shadow-md shadow-primary/20 scale-[1.02]'
                                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                            )}
                        >
                            <Wallet size={16} strokeWidth={2.5} />
                            <span className="hidden sm:inline">Finans</span>
                        </button>
                        <button
                            onClick={() => handleModuleChange('insaat')}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all duration-200',
                                activeModule === 'insaat'
                                    ? 'bg-gradient-to-r from-secondary to-secondary-dark text-white shadow-md shadow-secondary/20 scale-[1.02]'
                                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                            )}
                        >
                            <HardHat size={16} strokeWidth={2.5} />
                            <span className="hidden sm:inline">İnşaat</span>
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/10 rounded-lg">
                        <HardHat size={16} className="text-secondary" />
                        <span className="text-sm font-medium text-secondary hidden sm:inline">İnşaat Modülü</span>
                    </div>
                )}

                {/* Construction Selector - Desktop */}
                <div className="relative hidden md:block" ref={constructionMenuRef}>
                    <button
                        onClick={() => setConstructionMenuOpen(!constructionMenuOpen)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-tertiary hover:bg-bg-hover transition-colors border border-transparent hover:border-border-light"
                    >
                        <Building2 size={18} className="text-text-muted" />
                        <span className="text-sm font-medium text-text-primary max-w-[150px] truncate">
                            {currentConstructionName}
                        </span>
                        <ChevronDown size={16} className="text-text-muted" />
                    </button>

                    {constructionMenuOpen && (
                        <div className="absolute top-full left-0 mt-2 w-72 bg-bg-secondary rounded-xl shadow-xl border border-border-light py-2 z-50">
                            <button
                                onClick={handleSelectAll}
                                className={clsx(
                                    'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-tertiary transition-colors',
                                    showAllConstructions && 'bg-primary/5 text-primary'
                                )}
                            >
                                <div className={clsx("p-2 rounded-lg", showAllConstructions ? "bg-primary/10" : "bg-bg-tertiary")}>
                                    <Building2 size={18} className={showAllConstructions ? "text-primary" : "text-text-muted"} />
                                </div>
                                <span className="text-sm font-medium">Tüm İnşaatlar</span>
                                {showAllConstructions && <Check size={16} className="ml-auto text-primary" />}
                            </button>
                            <div className="h-px bg-border-light my-2 mx-4" />
                            <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                                {constructions.map((construction) => (
                                    <button
                                        key={construction.id}
                                        onClick={() => handleConstructionSelect(construction)}
                                        className={clsx(
                                            'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-tertiary transition-colors',
                                            !showAllConstructions && selectedConstruction?.id === construction.id && 'bg-primary/5'
                                        )}
                                    >
                                        <div className={clsx("p-2 rounded-lg", !showAllConstructions && selectedConstruction?.id === construction.id ? "bg-primary/10" : "bg-bg-tertiary")}>
                                            <Building2 size={18} className={!showAllConstructions && selectedConstruction?.id === construction.id ? "text-primary" : "text-text-muted"} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className={clsx("text-sm font-medium block truncate", !showAllConstructions && selectedConstruction?.id === construction.id ? "text-primary" : "text-text-primary")}>
                                                {construction.name}
                                            </span>
                                            <span className="text-xs text-text-muted">{construction.summary?.totalApartments || 0} daire • {construction.city}/{construction.district}</span>
                                        </div>
                                        {!showAllConstructions && selectedConstruction?.id === construction.id && (
                                            <Check size={16} className="flex-shrink-0 text-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2 md:gap-4">
                {/* Construction Selector - Mobile */}
                <div className="relative md:hidden mr-2" ref={constructionMenuRef}>
                    <button
                        onClick={() => setConstructionMenuOpen(!constructionMenuOpen)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-bg-tertiary hover:bg-bg-hover transition-colors border border-border-light shadow-sm"
                    >
                        <Building2 size={16} className="text-primary" />
                        <span className="hidden sm:block text-xs font-medium text-text-primary max-w-[100px] truncate">
                            {currentConstructionName}
                        </span>
                        <ChevronDown size={14} className={clsx("text-text-muted transition-transform duration-200", constructionMenuOpen && "rotate-180")} />
                    </button>

                    {constructionMenuOpen && (
                        <div className="fixed left-1/2 -translate-x-1/2 top-[calc(var(--header-height)+0.5rem)] w-[calc(100vw-2rem)] max-w-sm bg-bg-secondary rounded-xl shadow-2xl border border-border-light py-2 z-[9999] origin-top animate-in fade-in zoom-in-95 duration-200">
                            <button
                                onClick={handleSelectAll}
                                className={clsx(
                                    'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-tertiary transition-colors',
                                    showAllConstructions && 'bg-primary/5 text-primary'
                                )}
                            >
                                <div className={clsx("p-2 rounded-lg", showAllConstructions ? "bg-primary/10" : "bg-bg-tertiary")}>
                                    <Building2 size={18} className={showAllConstructions ? "text-primary" : "text-text-muted"} />
                                </div>
                                <span className="text-sm font-medium">Tüm İnşaatlar</span>
                                {showAllConstructions && <Check size={16} className="ml-auto text-primary" />}
                            </button>
                            <div className="h-px bg-border-light my-2 mx-4" />
                            <div className="max-h-[50vh] overflow-y-auto custom-scrollbar">
                                {constructions.map((construction) => (
                                    <button
                                        key={construction.id}
                                        onClick={() => handleConstructionSelect(construction)}
                                        className={clsx(
                                            'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-tertiary transition-colors',
                                            !showAllConstructions && selectedConstruction?.id === construction.id && 'bg-primary/5'
                                        )}
                                    >
                                        <div className={clsx("p-2 rounded-lg", !showAllConstructions && selectedConstruction?.id === construction.id ? "bg-primary/10" : "bg-bg-tertiary")}>
                                            <Building2 size={18} className={!showAllConstructions && selectedConstruction?.id === construction.id ? "text-primary" : "text-text-muted"} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className={clsx("text-sm font-medium block truncate", !showAllConstructions && selectedConstruction?.id === construction.id ? "text-primary" : "text-text-primary")}>
                                                {construction.name}
                                            </span>
                                            <span className="text-xs text-text-muted">{construction.summary?.totalApartments || 0} daire • {construction.city}</span>
                                        </div>
                                        {!showAllConstructions && selectedConstruction?.id === construction.id && (
                                            <Check size={16} className="flex-shrink-0 text-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {/* Notifications */}
                <div className="relative" ref={notificationRef}>
                    <NotificationBadge
                        onClick={() => setNotificationOpen(!notificationOpen)}
                        isOpen={notificationOpen}
                    />
                    {notificationOpen && (
                        <NotificationDropdown
                            onClose={() => setNotificationOpen(false)}
                            onMarkAllRead={handleMarkAllRead}
                        />
                    )}
                </div>

                {/* Theme Toggle - Desktop Only */}
                <button
                    onClick={() => dispatch(toggleTheme())}
                    className="hidden md:block p-2 rounded-lg hover:bg-bg-hover transition-colors"
                >
                    {theme === 'dark' ? (
                        <Sun size={20} className="text-text-secondary" />
                    ) : (
                        <Moon size={20} className="text-text-secondary" />
                    )}
                </button>

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                    <button
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-bg-hover transition-colors"
                    >
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <User size={16} className="text-primary" />
                        </div>
                        <div className="hidden md:block text-left">
                            <p className="text-sm font-medium text-text-primary">{user?.name || 'Kullanıcı'}</p>
                            <p className="text-xs text-text-muted">
                                {role === 'admin' ? 'Müteahhit' : 'Şantiye Şefi'}
                            </p>
                        </div>
                        <ChevronDown size={16} className="text-text-muted hidden md:block" />
                    </button>

                    {userMenuOpen && (
                        <div className="absolute top-full right-0 mt-2 w-48 bg-bg-secondary rounded-lg shadow-lg border border-border-light py-2 z-50">
                            <button
                                onClick={() => { navigate(role === 'admin' ? '/yonetici/profil' : '/santiye/profil'); setUserMenuOpen(false); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-bg-tertiary transition-colors"
                            >
                                <User size={18} className="text-text-muted" />
                                <span className="text-sm text-text-primary">Profil</span>
                            </button>
                            <button
                                onClick={() => { navigate(role === 'admin' ? '/yonetici/ayarlar' : '/santiye/ayarlar'); setUserMenuOpen(false); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-bg-tertiary transition-colors"
                            >
                                <Settings size={18} className="text-text-muted" />
                                <span className="text-sm text-text-primary">Ayarlar</span>
                            </button>

                            {/* Theme Toggle - Mobile Only */}
                            <button
                                onClick={() => { dispatch(toggleTheme()); setUserMenuOpen(false); }}
                                className="w-full flex md:hidden items-center justify-between px-4 py-2 text-left hover:bg-bg-tertiary transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    {theme === 'dark' ? (
                                        <Sun size={18} className="text-text-muted" />
                                    ) : (
                                        <Moon size={18} className="text-text-muted" />
                                    )}
                                    <span className="text-sm text-text-primary">
                                        {theme === 'dark' ? 'Açık Tema' : 'Koyu Tema'}
                                    </span>
                                </div>
                            </button>

                            <div className="h-px bg-border-light my-2" />
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-bg-tertiary transition-colors text-error"
                            >
                                <LogOut size={18} />
                                <span className="text-sm">Çıkış Yap</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
